const SDK_URL = "https://verify.msg91.com/otp-provider.js";
const SCRIPT_SELECTOR = 'script[data-msg91-otp-sdk="true"]';
const DEFAULT_CALLBACK_TIMEOUT_MS = 120000;

let loadFlight = null;
let initializationActive = false;
let callbackTimerId = null;

const safeEvent = (report, event) => {
  report?.(event);
};

export const loadMsg91Sdk = (report, timeoutMs = 12000) => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("SDK_BROWSER_UNAVAILABLE"));
  }
  if (typeof window.initSendOTP === "function") {
    safeEvent(report, "sdk-present");
    return Promise.resolve();
  }
  if (loadFlight) {
    safeEvent(report, "script-load-reused");
    return loadFlight;
  }

  loadFlight = new Promise((resolve, reject) => {
    const existing = document.querySelector(SCRIPT_SELECTOR);
    const script = existing || document.createElement("script");
    let settled = false;
    let timerId;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timerId);
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
      if (error) {
        loadFlight = null;
        reject(error);
        return;
      }
      resolve();
    };

    const onLoad = () => {
      if (typeof window.initSendOTP !== "function") {
        safeEvent(report, "sdk-api-missing");
        finish(new Error("SDK_API_MISSING"));
        return;
      }
      safeEvent(report, "script-loaded");
      finish();
    };

    const onError = () => {
      safeEvent(report, "script-error");
      finish(new Error("SDK_LOAD_ERROR"));
    };

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    timerId = window.setTimeout(() => {
      safeEvent(report, "script-timeout");
      finish(new Error("SDK_LOAD_TIMEOUT"));
    }, timeoutMs);

    if (!existing) {
      script.dataset.msg91OtpSdk = "true";
      script.src = SDK_URL;
      script.async = true;
      document.head.appendChild(script);
      safeEvent(report, "script-inserted");
    } else {
      safeEvent(report, "script-existing");
    }
  });

  return loadFlight;
};

export const invokeMsg91DefaultUi = ({
  widgetId,
  tokenAuth,
  identifier,
  onSuccess,
  onFailure,
  onTimeout,
  report,
  callbackTimeoutMs = DEFAULT_CALLBACK_TIMEOUT_MS,
}) => {
  if (initializationActive) {
    safeEvent(report, "init-already-active");
    throw new Error("SDK_INITIALIZATION_IN_PROGRESS");
  }
  if (typeof window.initSendOTP !== "function") {
    safeEvent(report, "sdk-api-missing");
    throw new Error("SDK_API_MISSING");
  }

  initializationActive = true;
  let settled = false;
  const releaseInitialization = () => {
    initializationActive = false;
    if (callbackTimerId !== null) {
      window.clearTimeout(callbackTimerId);
      callbackTimerId = null;
    }
  };
  const complete = (event, callback) => async (value) => {
    if (settled) return;
    settled = true;
    releaseInitialization();
    safeEvent(report, event);
    await callback(value);
  };
  try {
    window.initSendOTP({
      widgetId,
      tokenAuth,
      identifier,
      success: complete("success-callback", onSuccess),
      failure: complete("failure-callback", onFailure),
    });
    callbackTimerId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      releaseInitialization();
      safeEvent(report, "callback-timeout");
      onTimeout?.();
    }, callbackTimeoutMs);
    safeEvent(report, "init-called");
  } catch (error) {
    releaseInitialization();
    safeEvent(report, "init-throw");
    throw error;
  }
};

export const initializeMsg91DefaultUi = async (options) => {
  await loadMsg91Sdk(options.report);
  return invokeMsg91DefaultUi(options);
};

const isAccessToken = (value) => {
  if (typeof value !== "string") return false;
  const token = value.trim();
  const parts = token.split(".");
  return (
    token.length >= 20
    && parts.length === 3
    && parts.every((part) => /^[A-Za-z0-9_-]+$/.test(part))
  );
};

export const extractMsg91AccessToken = (value) => {
  if (isAccessToken(value)) return value.trim();
  if (!value || typeof value !== "object") return null;

  const isSuccessfulWidgetResponse = (
    value.type === "success"
    || value.invisibleVerified === true
  );
  if (!isSuccessfulWidgetResponse) return null;

  const findFieldToken = (candidate, fields, visited = new Set()) => {
    if (!candidate || typeof candidate !== "object" || visited.has(candidate)) return null;
    visited.add(candidate);

    for (const field of fields) {
      if (Object.prototype.hasOwnProperty.call(candidate, field)) {
        if (isAccessToken(candidate[field])) return candidate[field].trim();
        const nestedToken = findFieldToken(candidate[field], fields, visited);
        if (nestedToken) return nestedToken;
      }
    }
    for (const child of Object.values(candidate)) {
      if (!child || typeof child !== "object") continue;
      const token = findFieldToken(child, fields, visited);
      if (token) return token;
    }
    return null;
  };

  const namedToken = findFieldToken(value, [
    "access-token",
    "accessToken",
    "access_token",
    "token",
  ]);
  if (namedToken) return namedToken;
  return isAccessToken(value.message) ? value.message.trim() : null;
};

export const resetMsg91SdkForTests = () => {
  loadFlight = null;
  initializationActive = false;
  if (callbackTimerId !== null) {
    window.clearTimeout(callbackTimerId);
    callbackTimerId = null;
  }
  document.querySelectorAll(SCRIPT_SELECTOR).forEach((script) => script.remove());
  delete window.initSendOTP;
};